import { Component, OnInit, inject, ChangeDetectorRef, ChangeDetectionStrategy, DestroyRef } from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { decodeBase64OrReturn } from 'src/app/shared/utils/base64.util';
import { SearchService } from 'src/app/core/services/search/search.service';
import { NavigationContextService } from 'src/app/core/services/navigation-context/navigation-context.service';
import { EventsService } from '../../services/events.service';
import { of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { CardEventComponent } from 'src/app/shared/components/card-event/card-event.component';
import { CardCarouselComponent } from 'src/app/shared/components/card-carousel/card-carousel.component';
import { BreadcrumbDefaultComponent } from 'src/app/shared/components/breadcrumb-default/breadcrumb-default.component';
import { PaginatorModule } from 'primeng/paginator';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EventData } from '../../models/event.model';

interface EventCard extends EventData {
  day: string;
  month: string;
  description: string;
  src: string;
  title: string;
  city: string;
  state: string;
}

@Component({
    selector: 'app-event-filter',
    templateUrl: './event-filter.component.html',
    styleUrls: ['./event-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    CardEventComponent,
    CardCarouselComponent,
    BreadcrumbDefaultComponent,
    PaginatorModule,
    ButtonModule,
    RippleModule
]
})
export class EventFilterComponent implements OnInit {
  private readonly searchService = inject(SearchService);
  private readonly navigationContext = inject(NavigationContextService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly eventsService = inject(EventsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  searchText!: string;
  eventosEncontrados = 0;
  exibirTelaPrincipal = false;
  featuredEvents: EventCard[] = [];
  items: MenuItem[] = [];
  home!: MenuItem;
  filteredEvents: EventCard[] = [];
  section = '';
  allEvents: EventCard[] = [];
  pagedEvents: EventCard[] = [];

  readonly MONTH_FORMAT: Record<number, string> = {
    0: 'JAN',
    1: 'FEV',
    2: 'MAR',
    3: 'ABR',
    4: 'MAI',
    5: 'JUN',
    6: 'JUL',
    7: 'AGO',
    8: 'SET',
    9: 'OUT',
    10: 'NOV',
    11: 'DEZ',
  };

  ngOnInit(): void {
    this.items = [{ label: 'Resultado da busca' }];
    this.home = { icon: 'pi pi-home', routerLink: '/home' };

    this.getHighlights();
    this.observeSearchTerm();
  }

  private observeSearchTerm(): void {
    this.route.queryParamMap
      .pipe(
        map((params) => (params.get('q') ?? this.searchService.searchText()).trim()),
        filter(Boolean),
        distinctUntilChanged(),
        tap((term) => {
          this.searchText = term;
          this.searchService.setSearchText(term);
        }),
        switchMap((term) =>
          this.searchService.getSearchEvents(term).pipe(
            catchError(() => of({ data: [] }))
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response) => {
        this.eventosEncontrados = response.data.length;
        const formattedEvents = response.data.map((event) =>
          this.formatEvent(event)
        );
        this.filteredEvents = formattedEvents;
        this.allEvents = formattedEvents;
        this.exibirTelaPrincipal = response.data.length > 0;
        this.onPageChange({ first: 0, rows: 10 });
        
        this.filterFeaturedEvents();
        this.cdr.markForCheck();
      });
  }

  getHighlights(): void {
    this.eventsService.getHighlights()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
      (res: EventData[] | { data?: EventData[] }) => {
        if (!Array.isArray(res) && Array.isArray(res.data)) {
          this.featuredEvents = this.processEvents(res.data);
        } else if (Array.isArray(res)) {
          this.featuredEvents = this.processEvents(res);
        }
        
        // Filtrar destaques duplicados após carregar destaques
        this.filterFeaturedEvents();
      }
    );
  }

  private processEvents(events: EventData[]): EventCard[] {
    return events.map((event) => this.formatEvent(event));
  }

  private filterFeaturedEvents(): void {
    if (this.featuredEvents.length > 0 && this.allEvents.length > 0) {
      const foundIds = new Set(this.allEvents.map(e => e.id));
      this.featuredEvents = this.featuredEvents.filter(featured => !foundIds.has(featured.id));
    }
  }

  private formatEvent(event: EventData): EventCard {
    return {
      ...event,
      day: event.day?.padStart(2, '0') ?? '00',
      month: this.MONTH_FORMAT[Number(event.month) - 1] ?? event.month ?? '',
      description: decodeBase64OrReturn(event.description),
      src: event.image,
      title: event.name,
      city: event.city ?? event.address?.city ?? '',
      state: event.state ?? event.address?.state ?? '',
    };
  }

  // Base64 util consolidado em shared/utils/base64.util.ts

  setSection(section: string): void {
    this.section = section;
  }

  onPageChange(event: { first?: number; rows?: number }): void {
    const first = event.first ?? 0;
    const rows = event.rows ?? 10;
    this.pagedEvents = this.allEvents.slice(first, first + rows);
    
    // Força re-renderização dos cards
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  trackByEvent(index: number, item: EventCard): string | number {
    return item?.id ?? index;
  }

  back(): void {
    this.router.navigateByUrl(this.navigationContext.backTarget());
  }

  clearSearchAndGoHome(): void {
    this.searchService.clearSearchText();

    this.searchText = '';
    this.eventosEncontrados = 0;
    this.filteredEvents = [];
    this.allEvents = [];
    this.pagedEvents = [];
    this.exibirTelaPrincipal = false;

    this.router.navigateByUrl(this.navigationContext.backTarget());
  }

}
